import {
  describeLinkForLog,
  type OverlayPromptOptions,
  WORKBENCH_CALLER_ID,
} from '@hibro/shared';

export interface OpenLinkCommandLog {
  error(message: string, data?: Record<string, unknown>): void;
}

export interface OpenLinkCommandDeps {
  prompt: (options: OverlayPromptOptions) => Promise<string | null>;
  /** Translated prompt copy, read when a prompt is actually shown. */
  promptOptions: () => OverlayPromptOptions;
  openLink: (url: string, callerModuleId: string) => Promise<boolean>;
  log: OpenLinkCommandLog;
}

/**
 * Run `workbench.openLink` (见 20 / §5.9).
 *
 * No arguments → prompt for an address. A non-empty string argument is the
 * address and skips the prompt. A blank string, or a cancelled / blank prompt,
 * stops without a log: nothing was asked of `openLink`. A non-string argument
 * is a caller mistake — logged, not turned into a prompt.
 *
 * `openLink` resolving `false` is not a failure. That answer means no module
 * claimed the address and the runtime presented it. Only a throw is logged,
 * and the address in that log is host + path ({@link describeLinkForLog}),
 * never the query.
 */
export async function runOpenLinkCommand(
  args: readonly unknown[],
  deps: OpenLinkCommandDeps,
): Promise<void> {
  const url = await resolveOpenLinkUrl(args, deps);
  if (url === null) {
    return;
  }
  try {
    await deps.openLink(url, WORKBENCH_CALLER_ID);
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    deps.log.error('workbench.openLink failed', {
      link: describeLinkForLog(url),
      reason: err.message,
    });
  }
}

async function resolveOpenLinkUrl(
  args: readonly unknown[],
  deps: OpenLinkCommandDeps,
): Promise<string | null> {
  if (args.length === 0) {
    return askForUrl(deps);
  }
  const supplied = args[0];
  if (typeof supplied !== 'string') {
    deps.log.error('workbench.openLink was given a non-string address', {
      argType: typeof supplied,
    });
    return null;
  }
  const trimmed = supplied.trim();
  return trimmed.length > 0 ? trimmed : null;
}

async function askForUrl(deps: OpenLinkCommandDeps): Promise<string | null> {
  let entered: string | null;
  try {
    entered = await deps.prompt(deps.promptOptions());
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    deps.log.error('workbench.openLink prompt failed', {
      reason: err.message,
    });
    return null;
  }
  if (entered === null) {
    return null;
  }
  const trimmed = entered.trim();
  return trimmed.length > 0 ? trimmed : null;
}
